import type { SelectQueryBuilder } from 'kysely';
import first from 'lodash/first';
import groupBy from 'lodash/groupBy';
import uniqueId from 'lodash/uniqueId';
import { match, P } from 'ts-pattern';
import { qs } from '$lib/UrlHelper.js';
import type { RequestContainer } from '$lib/container/container.server';
import type { DB } from '$lib/kysely/db-types';
import { escapeForLike } from '$lib/kysely/kysely-utils';
import { assertNotNullable } from '$lib/utils/assert.js';
import { keepNotNull } from '$lib/utils/filterNotNull';
import { mapOrNull } from '$lib/utils/mapOrNull.js';
import { safe } from '$lib/utils/string.js';
import type { lazy, PartialOnUndefined } from '$lib/utils/types';
import type {
    CompanyRelationshipsArgs,
    ParticipantParticipantMediaForSlotArgs,
    QueryCompaniesArgs,
    QueryCompanyArgs,
} from '$gql/resolver-types.js';
import type { ParticipantInput } from '$gql/resolver-types.js';
import type { MediaRef } from '$gql/resolvers/MediaRef';
import {
    inboxNbUnreadResolve,
    type ParticipantRef,
} from '$gql/resolvers/ParticipantRef.js';
import type { Refs } from '$gql/resolvers/Refs';
import { zCompanyMediaSlotEnum } from '$gql/zod-generated';

const ID = uniqueId('CompanyResolver');

export class CompanyRef {
    constructor(
        private readonly _context: Pick<
            RequestContainer,
            | 'db'
            | 'dataLoaderRegistry'
            | 'urlHelper'
            | 'refs'
            | 'intl'
            | 'securityManager'
        >,
        private readonly _id: number,
        private readonly _options: { skipSecurity: boolean } = { skipSecurity: false },
    ) {}

    __typename = 'Company' as const;

    toParticipantInput(): ParticipantInput {
        return { companyId: this._id, userId: null };
    }

    withSkipSecurity(value: boolean): CompanyRef {
        return new CompanyRef(this._context, this._id, {
            ...this._options,
            skipSecurity: value,
        });
    }

    async load() {
        return this._context.dataLoaderRegistry().company().load(this._id);
    }

    ifNotExistParticipantReturnNull(this: this): Promise<ParticipantRef | null> {
        return this.ifNotExistReturnNull();
    }

    async ifNotExistReturnNull(this: this): Promise<this | null> {
        try {
            await this.load();
            return this;
        } catch {
            return null;
        }
    }

    id() {
        return this._id;
    }

    async address() {
        return (await this.load()).address;
    }

    async addressPostal() {
        return (await this.load()).address_postal;
    }

    async brandInfo() {
        return (await this.load()).brand_info;
    }

    async category() {
        const { category_id } = await this.load();
        assertNotNullable(category_id);
        return this._context.refs().category(category_id);
    }

    async childrenBoxMenu() {
        return (await this.load()).children_box_menu;
    }

    async childrenBoxTitle() {
        return (await this.load()).children_box_title;
    }

    async city() {
        return (await this.load()).city;
    }

    async clientDevis() {
        return Boolean((await this.load()).client_devis);
    }

    async companyCreatedDate() {
        return (await this.load()).company_created_date;
    }

    async coordinates() {
        const rows = await this._context
            .db()
            .selectFrom('coordinate')
            .select('id')
            .where('company_id', '=', this._id)
            .execute();

        return rows.map((r) => this._context.refs().coordinate(r.id));
    }

    async country(): Promise<string> {
        return (await this.load()).country;
    }

    async createdAt() {
        return (await this.load()).created_at;
    }

    async description() {
        return (await this.load()).description;
    }

    async district() {
        return (await this.load()).district;
    }

    async emailForContact() {
        return (await this.load()).email_for_contact;
    }

    async emailForQuote() {
        return (await this.load()).email_for_quote;
    }

    async facebookLink() {
        return (await this.load()).facebook_link;
    }

    async ficheSetting() {
        const loaded = await this._context
            .dataLoaderRegistry()
            .companyFicheSetting()
            .load({ companyId: this._id });

        return loaded == null ? null : this._context.refs().companyFicheSetting(this._id);
    }

    async googleLink() {
        return (await this.load()).google_link;
    }

    async h1() {
        return (await this.load()).h1;
    }

    async hasChildren() {
        const row = await this._context
            .db()
            .selectFrom('company')
            .select((eb) => eb.fn.count('id').as('count'))
            .where('company.parent_id', '=', this._id)
            .executeTakeFirst();

        const count = parseInt((row?.count ?? 0).toString());
        return count > 0;
    }

    async homepage() {
        return (await this.load()).homepage;
    }

    async inAlpha() {
        return Boolean((await this.load()).in_alpha);
    }

    async inPrint() {
        return Boolean((await this.load()).in_print);
    }

    async isClient() {
        return Boolean((await this.load()).is_client);
    }

    async isCertified() {
        return Boolean((await this.load()).is_certified);
    }

    async keywords() {
        return (await this.load()).keywords;
    }

    async latitude() {
        return mapOrNull((await this.load()).latitude, parseFloat);
    }

    async legalForm() {
        return (await this.load()).legal_form;
    }

    async linkedInLink() {
        return (await this.load()).linked_in_link;
    }

    async locked() {
        return Boolean((await this.load()).locked);
    }

    async longitude() {
        return mapOrNull((await this.load()).longitude, parseFloat);
    }

    async mainHomepage() {
        return (await this.load()).main_homepage;
    }

    async menuBrandInfo() {
        return (await this.load()).menu_brand_info;
    }

    async menuPraticalInfo() {
        return (await this.load()).menu_pratical_info;
    }

    async metaDesc() {
        return (await this.load()).meta_desc;
    }

    async metaTitle() {
        return (await this.load()).meta_title;
    }

    async name() {
        return (await this.load()).name;
    }

    async nationality() {
        return (await this.load()).nationality;
    }

    async nbVisits() {
        return (await this.load()).nb_visits;
    }

    async noTaxpayer() {
        return (await this.load()).no_taxpayer;
    }

    async noTradeRegistry() {
        return (await this.load()).no_trade_registry;
    }

    async notifyChangesByEmail() {
        return Boolean((await this.load()).notify_changes_by_email);
    }

    async online() {
        return Boolean((await this.load()).online);
    }

    async phoneForSms() {
        return (await this.load()).phone_for_sms;
    }

    async planWeight() {
        return (await this.load()).plan_weight;
    }

    async praticalInfo() {
        return (await this.load()).pratical_info;
    }

    async presentation() {
        return (await this.load()).presentation;
    }

    async presentationBbcode() {
        return (await this.load()).presentation_bbcode;
    }

    async printWeight() {
        return (await this.load()).print_weight;
    }

    async problematic() {
        return (await this.load()).problematic;
    }

    async rankInParent() {
        return (await this.load()).rank_in_parent;
    }

    async shareCapital() {
        return (await this.load()).share_capital;
    }

    async shoppingAdImage() {
        return this._context.refs().mediaOrNull((await this.load()).shopping_ad_image_id);
    }

    async shoppingDescription() {
        return (await this.load()).shopping_description;
    }

    async shoppingMenuName() {
        return (await this.load()).shopping_menu_name;
    }

    async shoppingTabName() {
        return (await this.load()).shopping_tab_name;
    }

    async shortName() {
        return (await this.load()).short_name;
    }

    async slug() {
        return (await this.load()).slug;
    }

    async socialObject() {
        return (await this.load()).social_object;
    }

    async squarePhoto() {
        return this._context.refs().mediaOrNull((await this.load()).square_photo_id);
    }

    async bannerPhoto() {
        return this._context.refs().mediaOrNull((await this.load()).banner_photo_id);
    }

    async bannerMobilePhoto() {
        return this._context
            .refs()
            .mediaOrNull((await this.load()).banner_mobile_photo_id);
    }

    async subCity() {
        return (await this.load()).sub_city;
    }

    async suspended() {
        return (await this.load()).suspended;
    }

    async textProblematic() {
        return (await this.load()).text_problematic;
    }

    async titleBrandInfo() {
        return (await this.load()).title_brand_info;
    }

    async titlePraticalInfo() {
        return (await this.load()).title_pratical_info;
    }

    async twitterLink() {
        return (await this.load()).twitter_link;
    }

    async unknownCity() {
        return (await this.load()).unknown_city;
    }

    async updatedAt() {
        return (await this.load()).updated_at;
    }

    async url() {
        const { id, slug, country } = await this.load();
        return (await this._context.urlHelper()).withAbsoluteUrl(true).company({
            id,
            slug,
            country,
        });
    }

    async valid() {
        return Boolean((await this.load()).valid);
    }

    async whatsapp() {
        return (await this.load()).whatsapp;
    }

    async whatsappInitialText() {
        return (await this.load()).whatsapp_initial_text;
    }

    async withComments() {
        return Boolean((await this.load()).with_comments);
    }

    async zip() {
        return (await this.load()).zip;
    }

    async menuIconBrandInfo() {
        return (await this.load()).menu_icon_brand_info;
    }

    async menuIconPraticalInfo() {
        return (await this.load()).menu_icon_pratical_info;
    }

    async shoppingMenuIcon() {
        return (await this.load()).shopping_menu_icon;
    }

    async googleMapItineraryUrl() {
        const { latitude, longitude, address, city, country } = await this.load();

        if (null != latitude && null != longitude) {
            const params = new URLSearchParams({
                daddr: safe`${latitude},${longitude}`,
            });
            return safe`https://maps.google.com${qs(params)}`;
        }

        const countryLabel = this._context.intl().countryLabel(country);

        const params = new URLSearchParams({
            daddr: safe`${address} ${city ?? ''} ${countryLabel}`,
        });
        return safe`https://maps.google.com${qs(params)}`;
    }

    async inboxNbUnread(): Promise<number> {
        return inboxNbUnreadResolve(this._context, this);
    }

    async participantName() {
        let { short_name, name } = await this.load();
        short_name = (short_name == null ? '' : short_name).trim();
        return short_name === '' ? name : short_name;
    }

    async participantPhoto() {
        return this.squarePhoto();
    }

    async participantMediaForSlot(
        args: PartialOnUndefined<ParticipantParticipantMediaForSlotArgs>,
    ): Promise<MediaRef | null> {
        const slot = zCompanyMediaSlotEnum.safeParse(args.slot);

        return await match(slot.data)
            .with('square', () => this.squarePhoto())
            .with('bannerMobile', () => this.bannerMobilePhoto())
            .with('banner', () => this.bannerPhoto())
            .with(P.nullish, async () => null)
            .exhaustive();
    }

    async participantProfileLink() {
        return this.url();
    }

    async participantTitle() {
        return this.description();
    }

    async participantCountry() {
        return this.country();
    }

    async participantCity() {
        return this.city();
    }

    async feature() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-Company-feature`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('company_feature')
                    .selectAll()
                    .where('company_id', 'in', ids)
                    .groupBy('company_id')
                    .execute();

                const rowsById = groupBy(rows, (r) => r.company_id);
                return ids.map((id) => first(rowsById[`${id}`]));
            },
        );

        return await loader.load(this._id);
    }

    async followedByMe() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();
        const userId = await this._context.securityManager().currentUserIdOrNull();

        if (userId == null) return false;

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-Company-followedByMe`,
            async (companyIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .selectAll()
                    .where('author_id', '=', userId)
                    .where(({ and, eb }) =>
                        and([
                            eb('target_company_id', 'in', companyIds),
                            eb('target_user_id', 'is', null),
                        ]),
                    )
                    .groupBy('target_company_id')
                    .execute();

                const rowsByCompanyId = groupBy(rows, (r) => r.target_company_id);
                return companyIds.map((companyId) =>
                    first(rowsByCompanyId[`${companyId}`]),
                );
            },
        );

        return (await loader.load(this._id)) != null;
    }

    async feedPostsCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${this.__typename}-feedPostsCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('feed_post')
                    .select(({ fn }) => [
                        'company_author_id',
                        fn.count<number>('id').as('count'),
                    ])
                    .where(({ and, eb }) =>
                        and([
                            eb('company_author_id', 'in', ids),
                            eb('user_author_id', 'is', null),
                        ]),
                    )
                    .groupBy('company_author_id')
                    .execute();

                const rowsById = groupBy(rows, (r) => r.company_author_id);
                return ids.map((id) => first(rowsById[`${id}`])?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async followersCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${this.__typename}-followersCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .select(({ fn }) => [
                        'target_company_id',
                        fn.count<number>('id').as('count'),
                    ])
                    .where(({ and, eb }) =>
                        and([
                            eb('target_company_id', 'in', ids),
                            eb('target_user_id', 'is', null),
                        ]),
                    )
                    .groupBy('target_company_id')
                    .execute();

                const rowsById = groupBy(rows, (r) => r.target_company_id);
                return ids.map((id) => first(rowsById[`${id}`])?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async followingCount() {
        return 0;
        /*const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${this.__typename}-followingCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .select(({ fn }) => ["author_id", fn.count<number>('id').as('count')])
                    .where('author_id', 'in', ids)
                    .groupBy('author_id')
                    .execute();

                const rowsById = groupBy(rows, (r) => r.author_id);
                return ids.map((id) => first(rowsById[`${id}`])?.count ?? 0);
            },
        );

        return await loader.load(this._id);*/
    }

    async managers() {
        const managers = await this._context
            .dataLoaderRegistry()
            .companyManager()
            .loadByCompanyId(this._id);

        return managers.map((m) => this._context.refs().companyManager(m.id));
    }

    async participantOnline() {
        try {
            const row = await this._context
                .dataLoaderRegistry()
                .inboxOnline()
                .loadByCompanyId(this.id());

            return this._context.refs().inboxOnline({ id: row.id });
        } catch {
            return null;
        }
    }

    async firstWebsite(this: this) {
        const websites = keepNotNull(
            await Promise.all(
                (await this.coordinates()).map(async (c) => {
                    if ((await c.type()) === 'website') {
                        return await c.content();
                    }
                    return null;
                }),
            ),
        );

        return websites.at(0) ?? null;
    }

    async socialNetworks(this: this) {
        const networks = await this._context
            .dataLoaderRegistry()
            .socialNetwork()
            .loadByCompanyId(this._id);

        return networks.map((n) => this._context.refs().socialNetwork(n.id));
    }

    async relationships(this: this, args: PartialOnUndefined<CompanyRelationshipsArgs>) {
        const { db, refs } = this._context;

        await this.canManageCompanyOrThrow();

        const query = db()
            .selectFrom('company_relationship')
            .where('company_id', '=', this._id)
            .orderBy('id', 'asc');

        return refs().pager(
            async () => {
                const total = await query
                    .select((eb) => eb.fn.countAll().as('count'))
                    .executeTakeFirst()
                    .then((row) => row?.count ?? 0)
                    .then((count) => parseInt(count.toString()));

                return { total, maxPerPage: args.maxPerPage, page: args.page };
            },
            async (pagerInfo) => {
                const results = await query
                    .select('company_relationship.id')
                    .offset((pagerInfo.page() - 1) * pagerInfo.maxPerPage())
                    .limit(pagerInfo.maxPerPage())
                    .execute();

                return results.map((row) => refs().companyRelationship(row.id));
            },
        );
    }

    private async canManageCompanyOrThrow() {
        const { securityManager } = this._context;

        if (
            this._options.skipSecurity ||
            (await securityManager().userCanManageCompany(this.id(), 'current')) ||
            (await securityManager().userHasRole('ROLE_ACCESS_ADMIN', 'current'))
        ) {
            return;
        } else {
            securityManager().throwSecurityMessage();
        }
    }
}

export class CompanyFicheSettingRef {
    constructor(
        private readonly _context: Pick<
            RequestContainer,
            'db' | 'dataLoaderRegistry' | 'urlHelper' | 'refs'
        >,
        private readonly _companyId: number,
    ) {}

    __typename = 'CompanyFicheSetting' as const;

    async load() {
        const loaded = await this._context
            .dataLoaderRegistry()
            .companyFicheSetting()
            .load({ companyId: this._companyId });

        if (loaded != null) {
            return loaded;
        }

        throw new Error('must be not null');
    }

    async id() {
        return (await this.load()).id;
    }

    async company() {
        return this._context.refs().company(this._companyId);
    }

    async enableEditMedia() {
        return Boolean((await this.load()).enable_edit_media);
    }

    async enableEditShopping() {
        return Boolean((await this.load()).enable_edit_shopping);
    }

    async showSquarePhoto() {
        return Boolean((await this.load()).show_square_photo);
    }

    async showBanner() {
        return Boolean((await this.load()).show_banner);
    }
}

export class QueryCompanyRef {
    constructor(private readonly _context: Pick<RequestContainer, 'refs' | 'db'>) {}

    company(this: this, args: PartialOnUndefined<QueryCompanyArgs>) {
        return this._context.refs().company(args.id);
    }

    companies(this: this, args: PartialOnUndefined<QueryCompaniesArgs>) {
        let query = this._context.db().selectFrom('company').select('id');

        if (args.q) {
            const qLike = `%${escapeForLike(args?.q).replaceAll(/ /g, '%')}%`;
            const qStartLike = `${escapeForLike(args?.q).replaceAll(/ /g, '%')}%`;
            query = query
                .where('name', 'like', qLike)
                .orderBy((eb) =>
                    eb.case().when('name', 'like', qStartLike).then(1).else(2).end(),
                )
                .orderBy('name asc');
        }

        return this.companyPager({
            maxPerPage: args.maxPerPage,
            page: args.page,
            query,
            refs: this._context.refs,
        });
    }

    async companyPager({
        maxPerPage,
        page,
        query,
        refs,
    }: {
        maxPerPage: number;
        page: number;
        query: SelectQueryBuilder<DB, 'company', unknown>;
        refs: lazy<Refs>;
    }) {
        return refs().pager(
            async () => {
                const total = await query
                    .select((eb) => eb.fn.countAll().as('count'))
                    .executeTakeFirst()
                    .then((row) => row?.count ?? 0)
                    .then((count) => parseInt(count.toString()));

                return {
                    total,
                    maxPerPage,
                    page,
                };
            },
            async (pagerInfo) => {
                const results = await query
                    .select('company.id')
                    .offset((pagerInfo.page() - 1) * pagerInfo.maxPerPage())
                    .limit(pagerInfo.maxPerPage())
                    .execute();

                return results.map((row) => refs().company(row.id));
            },
        );
    }
}
